import { ArrowRight, CaretDown, CaretUp, DotsThree, Pulse } from "@phosphor-icons/react";
import { cn } from "../lib/cn";
import { EyebrowBullet } from "../primitives/eyebrow-bullet";
import { ScoreGauge } from "./score-gauge";
import { BehaviourIcon } from "./snapshot/behaviour-icon";
import { SNAPSHOT_BEHAVIOURS } from "./snapshot/snapshot-tokens";

/**
 * ThriveScoreReadout, the at-a-glance score card on the patient
 * `/today` surface. Brought in line with the 2026-04-29 design system:
 * navy ink (not teal-900), serif weight 500 (not bold), gray-500 caption,
 * and the canonical card chrome shared with Outcome / Plan / Environment
 * / Summary so it sits as a peer in the column.
 *
 * Composition:
 *   [dot · THRIVE SCORE ... delta chip]
 *   [gauge | heading + caption + heartbeat chip]
 *   [CTA "Your Score →"]
 */
/** The five behaviours with their own reads, as Snapshot scores them. */
export type ThriveScoreBehaviours = ReadonlyArray<{ id: string; score: number | null }>;

export interface ThriveScoreReadoutProps {
  value: number;
  max?: number;
  /** Short narrative summary, e.g. "Steady this week" or "Starting out". */
  heading?: string;
  /** Secondary line, typically a call-to-action like "Tap for breakdown". */
  caption?: string;
  /** Signed delta vs previous period (e.g. +4, -2). Hidden when null. */
  delta?: number | null;
  /** When set, the card is a button and the inline CTA opens its target. */
  onClick?: () => void;
  /** Override the attribution chip text. */
  attribution?: string;
  /** CTA label rendered inline at the bottom. Defaults to "View snapshot". */
  ctaLabel?: string;
  /** Optional sub-line under the heading describing what the number is. Default empty so callers opt in. */
  methodologyAnchor?: string;
  /**
   * In "survival" mode the numeric
   * digit + gauge ring are suppressed - they're the wrong primitive
   * for a user whose sleep is fragmented and not under their control.
   * The card renders a floor-status block ("Above the floor" /
   * "Below the floor") in place of the ring, with the same heading +
   * caption + CTA chrome.
   *
   * In survival mode the number is the
   * wrong primitive entirely; a parent with fragmented sleep does not
   * need a score, they need permission to stop scoring."
   *
   * In "gathering" mode the ring goes for the same reason at the other end of
   * the journey: on day one there is nothing of the member's behind the
   * number, so the digit grades data they have not had the chance to produce.
   * A 12/100 next to "Just getting started" is the card arguing with itself.
   */
  mode?: "default" | "survival" | "gathering";
  /** Per-behaviour reads, so the card carries a Snapshot rather than five icons. */
  behaviours?: ThriveScoreBehaviours;
  /** Required when mode === "survival". True when the user is at or
   *  above the wellbeing floor; false when below it. Drives the
   *  floor-status pill copy + tone. */
  aboveFloor?: boolean;
  className?: string;
  /** When set, the "..." affordance becomes a button that calls this. */
  onMore?: () => void;
  onMoreLabel?: string;
}

function DeltaChip({ delta }: { delta: number }) {
  const positive = delta > 0;
  const zero = delta === 0;
  const tone = zero
    ? "border-border-secondary bg-surface-tertiary text-ink-secondary"
    : positive
      ? "border-leaf-soft bg-surface-accent-leaf text-ink-accent-leaf"
      : "border-orange-100 bg-orange-50 text-orange-700";
  const Icon = positive || zero ? CaretUp : CaretDown;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-xs tabular-nums",
        tone,
      )}
    >
      <Icon size={10} weight="fill" />
      {delta > 0 ? `+${delta}` : delta}
    </span>
  );
}

/**
 * The "Powered by Thrive Heartbeat" attribution chip was flagged in
 * user research as a B2B internal-architecture leak ("a trust badge
 * that wandered into my consumer app"). The component still ACCEPTS
 * an attribution label so demo / brand-marketing surfaces can opt in,
 * but the patient-side default below omits it. When `attribution`
 * is null/empty the chip doesn't render.
 */
function HeartbeatChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border-card bg-surface-elevated px-2.5 py-1 text-xs text-ink-secondary">
      <Pulse size={12} weight="bold" className="text-ink-brand-light" />
      <span>
        Powered by <span className="font-semibold text-ink-primary">{label}</span>
      </span>
    </span>
  );
}

/**
 * The five behaviours the score is built from, shown as a quiet color-coded
 * strip below the verdict. Previews the breakdown the `/snapshot` destination
 * opens into, and fills the otherwise-empty lower band of the card without
 * inventing live per-behaviour numbers (which the Today rollup does not carry).
 * Pure identity, zero new data.
 */
/*
 * The strip's own order. SNAPSHOT_BEHAVIOURS is ordered for the radar's
 * clockwise geometry, and reading it positionally here made a change to that
 * geometry silently reorder this card.
 */
const STRIP_ORDER = ["food", "movement", "sleep", "stress", "connection"].map((id) => {
  const meta = SNAPSHOT_BEHAVIOURS.find((b) => b.id === id);
  if (!meta) throw new Error(`unknown behaviour ${id}`);
  return meta;
});

/**
 * The five behaviours with their own reads: a Snapshot small enough to sit on
 * Today. A behaviour nothing has measured shows a dash, never a zero, because
 * the two say opposite things.
 */
function BehaviourStrip({ behaviours }: { behaviours?: ThriveScoreBehaviours }) {
  const scoreOf = (id: string) => behaviours?.find((b) => b.id === id)?.score ?? null;
  return (
    <div className="grid grid-cols-5 gap-1 border-t border-border-strong/25 pt-3.5">
      {STRIP_ORDER.map((b) => {
        const score = scoreOf(b.id);
        return (
          <div key={b.id} className="flex min-w-0 flex-col items-center gap-1">
            <BehaviourIcon
              name={b.iconName}
              size={16}
              weight="duotone"
              style={{ color: b.color }}
            />
            <span className="font-mono text-sm leading-none text-ink-primary [font-variant-numeric:tabular-nums]">
              {score ?? "-"}
            </span>
            <span className="truncate text-2xs tracking-[0.01em] text-ink-tertiary">{b.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export function ThriveScoreReadout({
  value,
  max = 100,
  heading = "Steady this week",
  // Caption simplification: the previous default was "Tap for
  // breakdown & wearables" - "wearables" felt like jargon to users
  // not steeped in the category. "Open the breakdown" is plain.
  caption = "Open the breakdown",
  delta = null,
  onClick,
  // Default attribution removed (was "Thrive Heartbeat"). Surfaces
  // that want the chip pass it explicitly. Patient surfaces leave
  // it blank.
  attribution = "",
  ctaLabel = "View snapshot",
  methodologyAnchor = "",
  mode = "default",
  aboveFloor,
  behaviours,
  className,
  onMore,
  onMoreLabel,
}: ThriveScoreReadoutProps) {
  const isSurvival = mode === "survival";
  const isGathering = mode === "gathering";
  // The descriptor line wins over the generic caption when a caller sets one.
  const subLine = methodologyAnchor || caption;
  const shellClass = cn(
    "@container group relative flex h-full flex-col rounded-2xl border border-border-card bg-surface-elevated bg-gradient-card p-4 shadow-card transition-shadow duration-200 sm:p-5",
    onClick && "cursor-pointer text-left hover:shadow-card-hover",
    className,
  );

  const header = (
    <div className="flex items-start justify-between gap-3">
      <EyebrowBullet>Snapshot</EyebrowBullet>
      <div className="flex items-center gap-1.5">
        {isGathering ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-track/60 px-2.5 py-1 font-sans font-medium text-xs text-ink-tertiary">
            <span className="h-[5px] w-[5px] rounded-full bg-teal-500 motion-safe:animate-[gentle-pulse_2.4s_ease-in-out_infinite]" />
            Gathering
          </span>
        ) : null}
        {delta !== null && !isSurvival && !isGathering && <DeltaChip delta={delta} />}
        {onMore && (
          <button
            type="button"
            aria-label={onMoreLabel ?? "More"}
            title={onMoreLabel ?? "More"}
            onClick={(e) => {
              e.stopPropagation();
              onMore();
            }}
            className="pointer-events-auto relative grid h-[26px] w-[26px] place-items-center rounded-sm text-ink-tertiary transition hover:bg-surface-track hover:text-ink-body"
          >
            <DotsThree size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );

  // In survival mode the numeric ring
  // is replaced with a floor-status block. Same chrome (eyebrow,
  // heading, caption, CTA) so the card still sits as a peer in the
  // grid; only the centre primitive changes.
  const survivalBody = (
    <div className="mt-4 flex flex-col gap-4">
      <div className="min-w-0">
        <p className="font-title text-lg font-medium leading-[1.2] tracking-[-0.015em] text-ink-primary">
          {heading}
        </p>
        <p
          className={cn(
            "mt-1 inline-flex items-center gap-1.5 font-sans font-medium text-xs",
            aboveFloor ? "text-ink-accent-leaf" : "text-attention-urgent-ink",
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "inline-block h-[5px] w-[5px] rounded-full",
              aboveFloor ? "bg-leaf-dark" : "bg-attention-urgent-ink",
            )}
          />
          {aboveFloor ? "On track" : "Needs attention"}
        </p>
      </div>
      {subLine ? <p className="text-sm leading-[1.4] text-ink-tertiary">{subLine}</p> : null}
      {attribution ? <HeartbeatChip label={attribution} /> : null}
      {/* The same footing as the other two modes. Without it the card is a
          status block and a void, and it still has to sit as a peer beside
          cards that fill their height. */}
      <BehaviourStrip behaviours={behaviours} />
    </div>
  );

  /*
   * Number-forward, one left axis. Mirrors the /snapshot destination hero
   * (a confident score read + verdict, calm and type-led) rather than a
   * centered dashboard gauge: a small quiet ring (no perpetual ripple, no
   * tick scale, fast count-up) sits beside a left-aligned verdict. Row by
   * default via the container query; only stacks (still left-aligned) on a
   * pathologically narrow card, so it reads the same at any width.
   */
  const defaultBody = (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex flex-col items-start gap-3.5 @[15rem]:flex-row @[15rem]:items-center @[15rem]:gap-4">
        <ScoreGauge
          value={value}
          max={max}
          size={72}
          stroke={8}
          ticks={0}
          pulse={false}
          countUpMs={700}
          label={`${value} out of ${max}`}
          renderCenter={(animated) => (
            <div className="flex flex-col items-center">
              <span className="font-sans text-2xl font-medium leading-none tracking-[-0.02em] text-ink-primary [font-variant-numeric:tabular-nums]">
                {animated}
              </span>
              <span className="font-sans font-medium text-xs text-ink-tertiary">/{max}</span>
            </div>
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="font-title text-lg font-medium leading-[1.2] tracking-[-0.015em] text-ink-primary">
            {heading}
          </p>
          {subLine ? (
            <p className="mt-1 text-sm leading-[1.4] text-ink-tertiary">{subLine}</p>
          ) : null}
          {attribution ? (
            <div className="mt-2">
              <HeartbeatChip label={attribution} />
            </div>
          ) : null}
        </div>
      </div>
      <BehaviourStrip behaviours={behaviours} />
    </div>
  );

  // Same chrome as the other two, minus the primitive that would be a verdict.
  const gatheringBody = (
    <div className="mt-4 flex flex-col gap-4">
      <div className="min-w-0">
        <p className="font-title text-lg font-medium leading-[1.2] tracking-[-0.015em] text-ink-primary">
          {heading}
        </p>
        {subLine ? <p className="mt-1 text-sm leading-[1.4] text-ink-tertiary">{subLine}</p> : null}
      </div>
      <BehaviourStrip behaviours={behaviours} />
    </div>
  );

  const body = isSurvival ? survivalBody : isGathering ? gatheringBody : defaultBody;

  const cta = (
    <div className="mt-auto pt-4">
      <span className="group/cta inline-flex w-max items-center gap-1.5 whitespace-nowrap rounded-lg border border-border-card bg-transparent px-3.5 py-2 font-sans text-sm font-medium tracking-[-0.005em] text-ink-brand transition-colors duration-200 group-hover:border-border-primary group-hover:bg-surface-secondary group-hover:text-teal-800">
        {ctaLabel}
        <ArrowRight
          size={12}
          weight="bold"
          className="transition-transform duration-200 group-hover/cta:translate-x-0.5"
        />
      </span>
    </div>
  );

  if (onClick) {
    // Stretched-button overlay: the card cannot be a <button> because the
    // header's onMore control is itself a button, and nested buttons are
    // invalid HTML. A full-bleed overlay button takes the primary click;
    // content is pointer-events-none so clicks fall through to it, while
    // onMore re-enables pointer events to sit above the overlay.
    return (
      <article className={cn(shellClass, "isolate")}>
        <button
          type="button"
          onClick={onClick}
          aria-label={ctaLabel ? `${heading}, ${ctaLabel}` : heading}
          className="absolute inset-0 z-0 rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
        />
        <div className="pointer-events-none relative z-10 flex h-full flex-col">
          {header}
          {body}
          {cta}
        </div>
      </article>
    );
  }
  return (
    <article className={shellClass}>
      {header}
      {body}
      {cta}
    </article>
  );
}
